import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getActiveCropDfpUuid, getActiveOperationUuid, getActiveUserId } from 'current/selectors';
import { useFields } from '../hooks/useFields';
import { useProductAssets } from '../hooks/useProductAssets';
import {
  ColumnDef,
  useReactTable,
  PaginationState,
  SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table';
import { useAsyncQueuer } from '@tanstack/react-pacer';
import { getAllOperationUuids } from 'operations/selectors';
import { Field } from '@tcc/shared-data-lib';
import { Table } from '@tcc/fds-table';
import FilterInput from './FilterInput';
import NoFieldsActions from './NoFieldsActions';
import FieldName from './FieldName';
import { FEATURE_NAMES } from 'constants/features';
import { Spinner, Checkbox } from '@tcc/crate';
import { getMessage } from 'util/locale';
import { useFormContext, useWatch } from 'react-hook-form';
import { addErrorToast, clearMessages, closeMessage } from 'messages';
import { getAllMessages } from 'messages/selectors';
import MessagePanel from 'messages/MessagePanel';
import { queryClient } from 'common/providers/QueryProvider';
import { getFieldYieldDataQueryOptions } from '../hooks/useFieldYieldData';

interface FieldSelectionProps {
  description?: string;
}

export const FieldSelection = ({ description }: FieldSelectionProps) => {
  // CSS
  const baseString = 'field-selection';
  const classString = classNames(`${baseString}`);

  // Get message to enable the toast functionality
  const getMessages = useAppSelector(getAllMessages);

  // Get Fields
  const selectedOperation = useAppSelector(getActiveOperationUuid);
  const allOperationsUuid = useAppSelector(getAllOperationUuids);

  const activeCropUuid = useAppSelector(getActiveCropDfpUuid);
  const dispatch = useAppDispatch();
  const activeUserId = useAppSelector(getActiveUserId);
  const {
    fields,
    fieldsUuids,
    isLoading: fieldsAreLoading,
  } = useFields(
    // This project manages the selected All operations by setting an empty string,
    // so we need to assume when it is an empty string it is all the operations
    selectedOperation ? [selectedOperation] : allOperationsUuid
  );
  const { productAssetsByField, isLoading: assetsAreLoading }
    = useProductAssets(Number(activeUserId), fieldsUuids);
  const isReady = !fieldsAreLoading && !assetsAreLoading;

  // Table Config
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Form Context
  const {
    setValue,
    formState: { errors },
    control,
  } = useFormContext();

  // Watch selectedFields from the form
  const watchedSelectedFields: { id: string; name: string }[] = useWatch({
    control,
    name: 'fields',
  });

  const fieldYieldAsyncQueuer = useAsyncQueuer((fielduuid: string) =>
    queryClient.prefetchQuery(getFieldYieldDataQueryOptions(fielduuid, activeCropUuid)),
  {
    concurrency: 10,
    started: true,
  });

  const columns: ColumnDef<Field>[] = useMemo(
    () => [
      {
        id: 'select',
        maxSize: 24,
        enableResizing: false,
        enableHiding: false,
        enableSorting: false,
        header: ({ table }) => {
          const preRows = table.getPrePaginationRowModel().rows;
          // Filter the field list to only select those with Advanced Scripting enabled
          const allFields = preRows.reduce((acc, { original }) => {
            if (
              productAssetsByField[original.id].includes(
                FEATURE_NAMES.ADVANCED_SCRIPTING
              )
            ) {
              acc.push(original);
            }
            return acc;
          }, []);

          const allSelected = allFields.length === watchedSelectedFields.length;
          const someSelected = watchedSelectedFields.length > 0 && !allSelected;

          return (
            <Checkbox
              checked={allSelected}
              onChange={() => {
                if (!allSelected) {
                  setValue('fields', allFields);
                  allFields.forEach((field) => fieldYieldAsyncQueuer.addItem(field.id));
                } else {
                  setValue('fields', []);
                }
              }}
              isIndeterminate={someSelected}
            />
          );
        },
        cell: ({ row: { original } }) => {
          const canAdvancedScripting = productAssetsByField[
          original.id
          ].includes(FEATURE_NAMES.ADVANCED_SCRIPTING);
          const isChecked = watchedSelectedFields.some(
            (field) => field.id === original.id
          );

          return (
            <Checkbox
              checked={isChecked}
              disabled={!canAdvancedScripting}
              onChange={() => {
                if (!isChecked) {
                  setValue('fields', [
                    ...watchedSelectedFields,
                    original,
                  ]);
                  queryClient.prefetchQuery(getFieldYieldDataQueryOptions(original.id, activeCropUuid));
                } else {
                  setValue(
                    'fields',
                    watchedSelectedFields.filter(
                      (field) => field.id !== original.id
                    )
                  );
                }
              }}
            />
          );
        },
      },
      {
        accessorKey: 'name',
        header: getMessage('bulk-fvss-table-column_field'),
        minSize: 100,
        cell: ({ row }) => {
          const canAdvancedScripting = productAssetsByField[row.original.id].includes(FEATURE_NAMES.ADVANCED_SCRIPTING);

          return (
            <FieldName
              name={row.getValue('name')}
              canAdvancedScripting={canAdvancedScripting}
              boundary={row.original.boundary}
            />
          );
        },
      },
      {
        header: getMessage('bulk-fvss-table-column_operation'),
        accessorKey: 'operationName',
        minSize: 100,
      },
      {
        accessorKey: 'farm.growerName',
        header: getMessage('bulk-fvss-table-column_client'),
        minSize: 100,
      },
      {
        accessorKey: 'farm.name',
        header: getMessage('bulk-fvss-table-column_farm'),
        minSize: 100,
      },
    ],
    [watchedSelectedFields, productAssetsByField, setValue, fieldYieldAsyncQueuer, activeCropUuid]
  );

  const table = useReactTable({
    data: fields,
    columns: columns,
    autoResetPageIndex: false,
    state: {
      globalFilter,
      pagination,
      sorting,
    },
    defaultColumn: {
      minSize: 150,
      maxSize: 1000,
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableMultiSort: true,
    isMultiSortEvent: () => true,
  });

  const emptyTableContent = !fields.length
    ? {
      message: getMessage('help-text__no-fields'),
      content: <NoFieldsActions />,
    }
    : {
      message: getMessage('help-text__search'),
    };
  const handleCloseToast = useCallback(
    (id: string) => {
      dispatch(closeMessage(id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (errors?.fields?.message || errors?.fields?.root?.message) {
      dispatch(
        addErrorToast(errors.fields?.message || errors.fields.root?.message)
      );
    }
    return () => {
      dispatch(clearMessages());
    };
  }, [errors.fields, dispatch, errors]);

  return (
    <Fragment>
      <MessagePanel
        autoDismiss={true}
        closeMessage={handleCloseToast}
        isToolbarShown={false}
        messages={getMessages}
      />
      {!!description && <p className={'step-description'} data-test-id={'step-1-description'}>
        {description}
      </p>}
      <div className={classString} data-test-id="table-wrapper">
        <div className={`${classString}-filter-input-wrapper`}>
          <FilterInput
            disabled={!isReady}
            value={globalFilter}
            onChange={setGlobalFilter}
          />
        </div>
        {/* Temporary spinner until the loading gets managed on fds-table */}
        {!isReady ? (
          <div className={`${classString}-loading-table`}>
            <Spinner testId="loading-spinner" />
          </div>
        ) : (
          <Table
            pageSizeOptions={[15, 25, 50]}
            table={table}
            isReady={isReady}
            emptyTableContent={emptyTableContent}
          />
        )}
      </div>
    </Fragment>
  );
};
